import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Collapse,
  Container,
  Group,
  LoadingOverlay,
  Paper,
  Popover,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core'
import '@mantine/core/styles.css'
import '@mantine/notifications/styles.css'
import { format } from 'date-fns'
import { CategorySection } from './CategorySection'
import { TicketDetails } from './TicketDetails'
import { DashboardStats } from './DashboardStats'
import { Ticket } from '@data/tickets/model/ticket'
import {
  BarChart3,
  Battery,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Filter,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Smartphone,
  Waves,
  Wifi,
  X,
  Zap,
} from 'lucide-react'
import { useTickets } from '@data/tickets/tickets'
import {
  TicketListView,
  TicketListViewRef,
} from '@components/main/Tickets/TicketListView'
import { useSelectedLocations } from '@data/locations/locations'

function TicketDashboard() {
  const [tickets, setTickets] = useState<Ticket[]>([] as Ticket[])
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string | null>('all')
  const [priorityFilter, setPriorityFilter] = useState<string | null>('all')
  const [issueTypeFilter, setIssueTypeFilter] = useState<string | null>('all')
  const [selectedDeviceIndex, setSelectedDeviceIndex] = useState<number>(0)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list')
  const [showStats, setShowStats] = useState(true)
  const [showFilters, setShowFilters] = useState(true)
  const [columnVisibilityVersion, setColumnVisibilityVersion] = useState(0)
  const handleColumnVisibilityChange = useCallback(() => {
    setColumnVisibilityVersion(v => v + 1)
  }, [])
  const listViewRef = useRef<TicketListViewRef>(null)

  const selectedLocations = useSelectedLocations()

  const { data: ticketsData, isLoading, isFetching, isPending, refetch } = useTickets({
    startDate: format(new Date(2025, 11, 31, 0), 'yyyy-MM-dd\'T\'HH:mm:ssxxx'),
    endDate: format(new Date(), 'yyyy-MM-dd\'T\'HH:mm:ssxxx'),
    status: ['open', 'in_progress', 'pending_review'],
    issueType: [],
  })

  // Filter tickets by customer locations and update when ticketsData or customerLocations change
  useEffect(() => {
    if (isLoading || isFetching || !ticketsData) return

    // Create a Set of valid location IDs for efficient lookup
    const validLocationIds = new Set(
      selectedLocations
        .filter(loc => loc.id) // Only include locations with an id
        .map(loc => Number(loc.id)), // Convert to number to match Ticket.locationId type
    )

    // Filter tickets to only include those with locationId in customerLocations
    const filteredByLocation: Ticket[] = ticketsData.filter((ticket: Ticket) =>
      validLocationIds.has(ticket.locationId),
    )

    setTickets(filteredByLocation)
  }, [ticketsData, isLoading, isFetching, selectedLocations])

// Handle refresh all tickets
  const handleRefreshTickets = async () => {
    await refetch()
  }

// Handle clear all filters
  const handleClearAllFilters = () => {
    setSearchQuery('')
    setStatusFilter('all')
    setPriorityFilter('all')
    setIssueTypeFilter('all')
    // Clear table column filters if in list view
    if (listViewRef.current) {
      listViewRef.current.clearFilters()
    }
  }

// Handle stat card click (for both status and issue type filters)
  const handleStatClick = (value: string | null) => {
    // Define possible values for each filter type
    const statusValues = ['all', 'open', 'in_progress', 'pending_review', 'resolved']
    const issueTypeValues = ['no_flow', 'connectivity', 'battery', 'power']

    if (value && statusValues.includes(value)) {
      // It's a status filter
      setStatusFilter(value)
      setIssueTypeFilter('all') // Reset issue type filter when clicking status
    } else if (value && issueTypeValues.includes(value)) {
      // It's an issue type filter
      setIssueTypeFilter(value)
      setStatusFilter('all') // Reset status filter when clicking issue type
    }
  }

// Get unique devices
  const uniqueDevices = useMemo(() => {
    const deviceMap = new Map<string, { id: string; name: string }>()
    tickets.forEach((ticket) => {
      if (!deviceMap.has(ticket.deviceId)) {
        deviceMap.set(ticket.deviceId, {
          id: ticket.deviceId,
          name: ticket.deviceName,
        })
      }
    })
    return Array.from(deviceMap.values())
  }, [tickets])

  const currentDevice = uniqueDevices[selectedDeviceIndex]

  const searchTerm = searchQuery.trim().toLowerCase()

  // Filter tickets once, then derive issue buckets in a single pass.
  const filteredTickets = useMemo(() => {
    return tickets.filter((ticket) => {
      const titleMatches = ticket.title?.toLowerCase().includes(searchTerm) ?? false
      const descriptionMatches = ticket.description?.toLowerCase().includes(searchTerm) ?? false
      const deviceNameMatches = ticket.deviceName.toLowerCase().includes(searchTerm)
      const matchesSearch = !searchTerm || titleMatches || descriptionMatches || deviceNameMatches

      const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter
      const matchesPriority = priorityFilter === 'all' || ticket.priority === priorityFilter
      const matchesIssueType = issueTypeFilter === 'all' || ticket.issueType === issueTypeFilter

      const matchesDevice = viewMode === 'grid'
        ? (currentDevice ? ticket.deviceId === currentDevice.id : false)
        : true

      return matchesSearch && matchesStatus && matchesPriority && matchesIssueType && matchesDevice
    })
  }, [issueTypeFilter, priorityFilter, searchTerm, statusFilter, tickets, viewMode])

  const ticketBuckets = useMemo(() => {
    const buckets = {
      no_flow: [] as Ticket[],
      connectivity: [] as Ticket[],
      battery: [] as Ticket[],
      power: [] as Ticket[],
      low_flow: [] as Ticket[],
    }

    for (const ticket of filteredTickets) {
      buckets[ticket.issueType].push(ticket)
    }

    return buckets
  }, [filteredTickets])

  const flowTickets = ticketBuckets.no_flow
  const connectivityTickets = ticketBuckets.connectivity
  const batteryTickets = ticketBuckets.battery
  const powerTickets = ticketBuckets.power

  return (
    // <MantineProvider>
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
      {/* Header */}
      <Container size="xl" fluid>
        <Paper
          shadow="xs"
          p="md"
          mb="sm"
          radius={0}
        >
          <Container size="xl" fluid>
            <Title
              order={1}
              mb="xs"
              ta="center"
            >
              Resolution Monitoring Centre
            </Title>
          </Container>
        </Paper>
      </Container>

      <Container size="xl" fluid>
        {/* Statistics */}
        <Paper
          shadow="sm"
          p="xs"
          radius="md"
          withBorder
          mb="xs"
        >
          <Box pos="relative">
            <LoadingOverlay
              visible={isPending}
              zIndex={1000}
              overlayProps={{ radius: 'sm', blur: 2 }}
              loaderProps={{ color: 'blue', type: 'bars' }}
            />
            <Group
              justify="flex-start"
              mb="xs"
              px="xs"
              style={{ cursor: 'pointer', userSelect: 'none' }}
              onClick={() => setShowStats(v => !v)}
            >
              <BarChart3 size={16} />
              <Text
                size="sm"
                fw={500}
              >
                Stats:
              </Text>

              <Tooltip label={'Show/Hide Ticket Stats'}>
                <ChevronDown
                  size={16}
                  style={{
                    color: 'var(--mantine-color-dimmed)',
                    transform: showStats ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'transform 0.2s ease',
                  }}
                />
              </Tooltip>
            </Group>
            <Collapse expanded={showStats}>
              <DashboardStats
                tickets={tickets}
                onStatClick={handleStatClick}
                activeFilter={statusFilter !== 'all' ? statusFilter : issueTypeFilter}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
              />
            </Collapse>
          </Box>
        </Paper>

        {/* Filters */}
        <Box pos="relative">
          <LoadingOverlay
            visible={isPending}
            zIndex={1000}
            overlayProps={{ radius: 'sm', blur: 2 }}
            loaderProps={{ color: 'blue', type: 'bars' }}
          />
          <Paper
            shadow="sm"
            p="md"
            radius="md"
            withBorder
            mb="sm"
          >
            <Stack gap="sm">
              <Group justify="space-between" wrap="nowrap">
                <Group
                  gap="xs"
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => setShowFilters(v => !v)}
                >
                  <Filter size={16} />
                  <Text size="sm" fw={500}>Filters:</Text>
                  <Tooltip label={'Show/Hide Ticket Filters'}>
                    <ChevronDown
                      size={14}
                      style={{
                        color: 'var(--mantine-color-dimmed)',
                        transform: showFilters ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform 0.2s ease',
                      }}
                    />
                  </Tooltip>
                </Group>

              </Group>

              <Collapse expanded={showFilters}>
                <Stack gap="sm">
                  <Group gap="md" align="flex-end" wrap="wrap">
                    <TextInput
                      placeholder="Search tickets..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.currentTarget.value)}
                      leftSection={<Search size={16} />}
                      style={{ flex: '1 1 300px' }}
                    />

                    <Select
                      placeholder="Filter by status"
                      value={statusFilter}
                      onChange={setStatusFilter}
                      data={[
                        { value: 'all', label: 'All Statuses' },
                        { value: 'open', label: 'Open' },
                        { value: 'in_progress', label: 'In Progress' },
                        { value: 'resolved', label: 'Resolved' },
                      ]}
                      style={{ width: 180 }}
                    />

                    <Select
                      placeholder="Filter by priority"
                      value={priorityFilter}
                      onChange={setPriorityFilter}
                      data={[
                        { value: 'normal', label: 'Normal' },
                        { value: 'high', label: 'High' },
                      ]}
                      style={{ width: 180 }}
                    />

                    <Button
                      variant="light"
                      leftSection={<RefreshCw size={16} />}
                      onClick={handleRefreshTickets}
                      loading={isFetching}
                      color="blue"
                    >
                      Refresh
                    </Button>

                    <Button
                      variant="light"
                      leftSection={<X size={16} />}
                      onClick={handleClearAllFilters}
                      color="red"
                    >
                      Clear Filters
                    </Button>

                    {viewMode === 'list' && (
                      <Popover
                        position="bottom-end"
                        withArrow
                        shadow="md"
                        closeOnClickOutside
                      >
                        <Popover.Target>
                          <Button
                            size="sm"
                            variant="light"
                            leftSection={
                              <SlidersHorizontal size={14} />
                            }
                          >
                            Columns
                          </Button>
                        </Popover.Target>
                        <Popover.Dropdown>
                          <Stack gap="xs" style={{ minWidth: 160 }}>
                            <Text
                              size="xs"
                              fw={600}
                              c="dimmed"
                              tt="uppercase"
                              mb={4}
                            >
                              Toggle columns
                            </Text>
                            {/* columnVisibilityVersion ensures a re-render after each toggle */}
                            {columnVisibilityVersion >= 0 && (listViewRef.current?.getHideableColumns() ?? []).map(col => (
                              <Checkbox
                                key={col.id}
                                label={col.label}
                                checked={col.visible}
                                onChange={col.toggle}
                                size="sm"
                              />
                            ))}
                          </Stack>
                        </Popover.Dropdown>
                      </Popover>
                    )}

                  </Group>

                  {/* Device Navigation */}
                  {viewMode === 'grid' && uniqueDevices.length > 0 ? (
                    <Paper p="sm" withBorder style={{ backgroundColor: '#f8f9fa' }}>
                      <Group justify="space-between" align="center">
                        <Group gap="xs">
                          <Smartphone size={18} color="#228be6" />
                          <Text size="sm" fw={500}>
                            Viewing Device:
                          </Text>
                        </Group>

                        <Group gap="md">
                          <Button
                            variant="light"
                            size="sm"
                            leftSection={<ChevronLeft size={16} />}
                            onClick={() => setSelectedDeviceIndex((prev) => (prev > 0 ? prev - 1 : uniqueDevices.length - 1))}
                            disabled={uniqueDevices.length <= 1}
                          >
                            Previous
                          </Button>

                          <Stack gap={0} align="center">
                            <Badge color="blue" size="lg" variant="filled">
                              {`${currentDevice.id} - ${currentDevice.name}`}
                            </Badge>
                            <Text size="xs" c="dimmed" mt={4}>
                              {selectedDeviceIndex + 1} of {uniqueDevices.length} devices
                            </Text>
                          </Stack>

                          <Button
                            variant="light"
                            size="sm"
                            rightSection={<ChevronRight size={16} />}
                            onClick={() => setSelectedDeviceIndex((prev) => (prev < uniqueDevices.length - 1 ? prev + 1 : 0))}
                            disabled={uniqueDevices.length <= 1}
                          >
                            Next
                          </Button>
                        </Group>

                        <Badge color="gray" variant="light">
                          {filteredTickets.length} {filteredTickets.length === 1 ? 'issue' : 'issues'}
                        </Badge>
                      </Group>
                    </Paper>
                  ) : viewMode === 'grid' ? (
                    <Paper p="sm" withBorder style={{ backgroundColor: '#fff3cd' }}>
                      <Group gap="xs" justify="center">
                        <Smartphone size={18} color="#856404" />
                        <Text size="sm" fw={500} c="#856404">
                          No devices with tickets found
                        </Text>
                      </Group>
                    </Paper>
                  ) : null}
                </Stack>
              </Collapse>
            </Stack>
          </Paper>
        </Box>

        {/* Categories Grid */}
        {viewMode === 'grid' && (
          <Box pos="relative">
            <LoadingOverlay
              visible={isPending}
              zIndex={1000}
              overlayProps={{ radius: 'sm', blur: 2 }}
              loaderProps={{ color: 'blue', type: 'bars' }}
            />
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
                gap: '1.5rem',
                marginBottom: '2rem',
              }}
            >
              {flowTickets.length > 0 ?
                <CategorySection
                  title="Flow Issues"
                  icon={Waves}
                  tickets={flowTickets}
                  onTicketClick={setSelectedTicket}
                  color="#228be6"
                /> : null}
              {connectivityTickets.length > 0 ?
                <CategorySection
                  title="Connectivity Issues"
                  icon={Wifi}
                  tickets={connectivityTickets}
                  onTicketClick={setSelectedTicket}
                  color="#7950f2"
                /> : null}

              {batteryTickets.length > 0 ?
                <CategorySection
                  title="Battery Issues"
                  icon={Battery}
                  tickets={batteryTickets}
                  onTicketClick={setSelectedTicket}
                  color="#40c057"
                /> : null}

              {powerTickets.length > 0 ?
                <CategorySection
                  title="Power Issues"
                  icon={Zap}
                  tickets={powerTickets}
                  onTicketClick={setSelectedTicket}
                  color="#fd7e14"
                /> : null}

            </div>
          </Box>
        )}

        {/* Ticket List View */}
        {viewMode === 'list' && (
          <Box pos="relative">
            <LoadingOverlay
              visible={isPending}
              zIndex={1000}
              overlayProps={{ radius: 'sm', blur: 2 }}
              loaderProps={{ color: 'blue', type: 'bars' }}
            />
            <TicketListView
              tickets={filteredTickets}
              onTicketClick={setSelectedTicket}
              ref={listViewRef}
              statusFilter={statusFilter}
              priorityFilter={priorityFilter}
              issueTypeFilter={issueTypeFilter}
              onClearStatusFilter={() => setStatusFilter('all')}
              onClearPriorityFilter={() => setPriorityFilter('all')}
              onClearIssueTypeFilter={() => setIssueTypeFilter('all')}
              onColumnVisibilityChange={handleColumnVisibilityChange}
              searchTerm={searchTerm}
            />
          </Box>
        )}
      </Container>

      {/* Ticket Details Modal */}
      {selectedTicket ?
        <TicketDetails
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
        />
        : null}
    </div>
    // </MantineProvider>
  )
}

export default TicketDashboard
